import { useEffect } from 'react'

// Ambient wave background for the operator UI. The zero-dep reactive-backgrounds
// library (vendored at public/reactive-bg/, loaded by the classic <script> tags
// in index.html) mounts a fixed, pointer-events:none canvas behind the app and
// leans the particle field toward the cursor. Hue 244 tracks the app accent.
//
// Config is the wave preset David picked from the demo panel:
//   preset: 'wave', count: 80, tempo: 0.3, repel: 70, size: 3.4, accent: 244
type CanvasParticlesInstance = { destroy?: () => void; canvas?: HTMLCanvasElement }
type ReactiveBGGlobal = {
  CanvasParticles: new (target: Element, opts: Record<string, unknown>) => CanvasParticlesInstance
}

// How strongly the wave reads through the UI. The panels above it are now
// TRANSLUCENT (see --glass), so the same number reads considerably stronger than
// it used to — this came DOWN when the chrome opened up, not up. The goal is a
// texture you notice once, not a pattern competing with the numbers over it.
const WAVE_OPACITY = 0.07

// The preset's `count` is a particle COUNT, not a density — 80 of them spread
// across a 1440px desktop read as an airy wave, and the same 80 squeezed into a
// 390px portrait phone read as a crowded dotted texture (reported from a real
// handset). Scale the count with viewport width so the SPACING stays put, and
// keep a floor so a narrow window still reads as a field rather than a few
// stray specks.
const REFERENCE_WIDTH = 1440
const REFERENCE_COUNT = 80
const MIN_COUNT = 24
function particleCount(width: number): number {
  return Math.max(MIN_COUNT, Math.round((width / REFERENCE_WIDTH) * REFERENCE_COUNT))
}

export function ReactiveBackground() {
  useEffect(() => {
    // Honor prefers-reduced-motion (WCAG 2.3.3 / the barometer's a11y gate): the
    // wave is decorative, so under `reduce` we mount nothing at all. Re-evaluated
    // live if the OS preference flips.
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    let fx: CanvasParticlesInstance | null = null
    const teardown = () => { fx?.destroy?.(); fx = null }
    const apply = () => {
      teardown()
      if (mq.matches) return // reduced motion requested — no animated background
      const RB = (window as unknown as { ReactiveBG?: ReactiveBGGlobal }).ReactiveBG
      if (!RB?.CanvasParticles) return // library not loaded (e.g. script blocked) — app renders fine without it
      fx = new RB.CanvasParticles(document.body, {
        preset: 'wave',
        count: particleCount(window.innerWidth),
        tempo: 0.3,
        repel: 70,
        size: 3.4,
        accent: 244,
        lean: 0, // lock the formation in place — the wave animates but doesn't drift toward the cursor
      })
      // The canvas is tagged so a surface can turn it down. The story page
      // does: soft bokeh is the one layer on it that could belong to any dark
      // SaaS site, and on that page's deliberately empty screens it became
      // the dominant visual — competing with a 2px drafting line. The story's
      // own grounds (blueprint grid, dot matrix) are its materials.
      if (fx.canvas) {
        fx.canvas.classList.add('reactive-bg')
        fx.canvas.style.opacity = String(WAVE_OPACITY)
      }
    }
    apply()
    mq.addEventListener('change', apply)
    // Rotating a phone changes the width by ~2.5x. Rebuild only when the count
    // actually moves, so a resize drag does not thrash the canvas.
    let built = particleCount(window.innerWidth)
    let t: ReturnType<typeof setTimeout> | undefined
    const onResize = () => {
      clearTimeout(t)
      t = setTimeout(() => {
        const next = particleCount(window.innerWidth)
        if (next !== built) { built = next; apply() }
      }, 200)
    }
    window.addEventListener('resize', onResize)
    return () => {
      clearTimeout(t)
      window.removeEventListener('resize', onResize)
      mq.removeEventListener('change', apply)
      teardown() // clean teardown (also handles StrictMode's double-mount in dev)
    }
  }, [])
  return null
}
